import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Box, TextField, Typography, Button } from "@mui/material";
import { useTranslation } from "react-i18next";
import AppBackground from "../components/layout/AppBackground";
import { useAuth } from "../hooks/AuthContext";

const ActivateAccount = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const { login } = useAuth();
  const { i18n } = useTranslation("common");
  const [email, setEmail] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const language = i18n.language?.startsWith("en") ? "en" : "es";

  useEffect(() => {
    fetch(`${process.env.REACT_APP_API_URL}/auth/activate-account/${token}`)
      .then(async (response) => {
        const data = await response.json();
        if (!response.ok) throw new Error(data.msg || "Invalid activation link.");
        setEmail(data.email);
        await i18n.changeLanguage(data.language === "en" ? "en" : "es");
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [i18n, token]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");
    if (newPassword !== confirmPassword) {
      setError(language === "en" ? "Passwords do not match." : "Las contraseñas no coinciden.");
      return;
    }
    if (newPassword.length < 4) {
      setError(language === "en" ? "Password must be at least 4 characters." : "La contraseña debe tener al menos 4 caracteres.");
      return;
    }

    setSubmitting(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/auth/activate-account/${token}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ new_password: newPassword }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.msg || (language === "en" ? "The account could not be activated." : "No se pudo activar la cuenta."));
      login(data.access_token);
      navigate("/", { replace: true });
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AppBackground sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
      <Box
        component="form"
        onSubmit={handleSubmit}
        sx={{ backgroundColor: "#212121", color: "#fff", borderRadius: 4, p: 4, maxWidth: 420, width: "100%", textAlign: "center" }}
      >
        <Typography variant="h5" gutterBottom>
          {language === "en" ? "Activate your account" : "Activá tu cuenta"}
        </Typography>
        <Typography variant="body2" sx={{ color: "#ccc", mb: 2 }}>
          {language === "en"
            ? "Create your password to start practicing with CarpinchIA."
            : "Creá tu contraseña para empezar a practicar con CarpinchIA."}
        </Typography>
        <TextField label="Email" value={email} fullWidth margin="normal" InputProps={{ readOnly: true }} />
        <TextField
          label={language === "en" ? "New password" : "Nueva contraseña"}
          type="password"
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          fullWidth
          margin="normal"
          inputProps={{ maxLength: 50 }}
        />
        <TextField
          label={language === "en" ? "Confirm password" : "Confirmar contraseña"}
          type="password"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          fullWidth
          margin="normal"
          inputProps={{ maxLength: 50 }}
        />
        {error && <Typography color="error" sx={{ mt: 2 }}>{error}</Typography>}
        <Button
          type="submit"
          variant="contained"
          fullWidth
          sx={{
            mt: 3,
            borderRadius: 999,
            py: 1.2,
            "&.Mui-disabled": {
              backgroundColor: "#8e5bd6",
              color: "#ffffff",
              opacity: 1,
            },
          }}
          disabled={loading || submitting || !email}
        >
          {submitting
            ? (language === "en" ? "Activating..." : "Activando...")
            : (language === "en" ? "Activate account" : "Activar cuenta")}
        </Button>
      </Box>
    </AppBackground>
  );
};

export default ActivateAccount;
